import {
  ChangeDetectionStrategy,
  Component,
  input,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  ControlsOf,
  FormArray,
  FormControl,
  FormGroup,
} from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@vinnova/translation';
import { debounceTime } from 'rxjs';
import {
  FormBudget,
  FormForm,
  FormQuestion,
  FormQuestionRepeater,
  FormQuestionStandard,
  FormRepeatable,
} from '../../core/models/form-group-mapping.model';
import { Form } from '../../core/models/form.model';
import { QuestionStructureRepeater } from '../../core/models/question/question-repeater.model';
import { QuestionType } from '../../core/models/question/question-type.model';
import { Question } from '../../core/models/question/question.model';
import { Section } from '../../core/models/section.model';
import { RendererFormsRepository } from '../../core/repositories/renderer-forms.repository';
import { QuestionNumberService } from '../../core/services/question-number.service';
import { TranslatePipe } from '../../pipes/translate/translate.pipe';
import { ValidationList } from './validation.model';

@UntilDestroy()
@Component({
  selector: 'lib-aside-validation',
  templateUrl: './aside-validation.component.html',
  styleUrls: ['./aside-validation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AsideValidationComponent implements OnInit {
  formGroup = input<FormGroup<ControlsOf<FormForm>>>();
  form = input<Form>();
  showSuccess = input<boolean>();
  successMessage = input<string>();

  validationList: WritableSignal<ValidationList> = signal(undefined);

  constructor(
    private questionNumberService: QuestionNumberService,
    private translatePipe: TranslatePipe,
    private translateService: TranslateService,
    private translate: TranslocoService,
    private rendererFormsRepository: RendererFormsRepository,
  ) {}

  ngOnInit(): void {
    this.formGroup()
      .statusChanges.pipe(debounceTime(200), untilDestroyed(this))
      .subscribe(() => {
        this.setValidationList();
      });

    this.translateService.langChanges$
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.setValidationList();
      });
  }

  goToSection(sectionId: string) {
    const accordion = document.querySelector(
      `[aria-controls="accordion-${sectionId}"`,
    ) as HTMLElement;
    if (accordion.getAttribute('aria-expanded') === 'false') {
      const collapsible = document.querySelector(`#accordion-${sectionId}`);
      accordion.click();
      collapsible.addEventListener(
        'shown.bs.collapse',
        function scrollToSection() {
          accordion.scrollIntoView({ block: 'center' });
          accordion.focus();
          collapsible.removeEventListener('shown.bs.collapse', scrollToSection);
        },
      );
    } else {
      accordion.scrollIntoView({ block: 'center' });
      accordion.focus();
    }
  }

  goToQuestion(questionId: string, sectionId: string, repeaterId?: number) {
    const accordion = document.querySelector(
      `[aria-controls="accordion-${sectionId}"`,
    ) as HTMLElement;

    const scrollToQuestion = () => {
      const selectorStr = `#question-${questionId}`;
      const question = (
        repeaterId
          ? document.querySelectorAll(selectorStr)[repeaterId]
          : document.querySelector(selectorStr)
      ) as HTMLElement;

      let firstFocusable = question.querySelector(
        'input[aria-invalid="true"], textarea[aria-invalid="true"], .projektpart-invalid .expand-btn, .classification-invalid button, .projektpart-invalid button',
      ) as HTMLElement;

      const isBudget =
        question.children?.[0]?.localName === 'lib-question-budget';
      const isClassification =
        question.children?.[0]?.localName === 'lib-question-classification';

      if (isClassification) {
        firstFocusable = question.querySelector('.vinnova-button-secondary');
      }

      if (!firstFocusable) {
        firstFocusable = isBudget
          ? (question.querySelector('.invalid-feedback') as HTMLElement)
          : (question.querySelector('input, textarea, button') as HTMLElement);
      }
      const focusElement = firstFocusable ?? question;
      focusElement.scrollIntoView({ block: 'center' });
      if (!isBudget) {
        focusElement.focus();
        focusElement.classList.add('focus-visible');
      }
    };

    if (accordion.getAttribute('aria-expanded') === 'false') {
      const collapsible = document.querySelector(`#accordion-${sectionId}`);
      accordion.click();
      collapsible.addEventListener(
        'shown.bs.collapse',
        function scrollToSection() {
          scrollToQuestion();
          collapsible.removeEventListener('shown.bs.collapse', scrollToSection);
        },
      );
    } else {
      scrollToQuestion();
    }
  }

  private setValidationList() {
    const validationList = {
      sections: [],
    };
    const hideSections = this.rendererFormsRepository.getHideSections();
    for (const sectionFormGroup of this.formGroup().get('sections').controls) {
      if (
        sectionFormGroup.valid ||
        hideSections?.includes(sectionFormGroup.getRawValue().id)
      ) {
        continue;
      }
      const section = this.form().sections.find(
        (s) => s.id === sectionFormGroup.getRawValue().id,
      );
      const sectionNumber = this.form().structure.order.indexOf(section.id) + 1;
      const validationSection = {
        title: this.getSectionTitle(section, sectionNumber),
        id: section.id,
        questions: [],
      };

      for (const questionId of section.structure.order.flat()) {
        const questionFormGroup = sectionFormGroup
          .get('questions')
          .controls.find((q) => q.getRawValue().id === questionId);
        const question = section.questions.find((q) => q.id === questionId);

        const repeaterRequired =
          question.type === QuestionType.Repeater &&
          this.isRepeaterRequiredFailed(
            questionFormGroup as unknown as FormControl<
              ControlsOf<FormQuestionRepeater>
            >,
            question.structure as QuestionStructureRepeater,
          );

        if (questionFormGroup.valid) {
          continue;
        }

        this.setQuestionFailed(
          question,
          questionFormGroup,
          section,
          validationSection,
          repeaterRequired,
        );
      }

      validationList.sections.push(validationSection);
    }
    validationList.sections.sort((a, b) =>
      parseInt(a.title.slice(0, a.title.indexOf('.'))) >
      parseInt(b.title.slice(0, b.title.indexOf('.')))
        ? 1
        : -1,
    );
    this.validationList.set(validationList);
  }

  private setQuestionFailed(
    question: Question,
    questionFormGroup: FormGroup<ControlsOf<FormQuestion>>,
    section: Section,
    validationSection: {
      title: string;
      id: string;
      questions: { title: string; id: string; isRequiredFailed: boolean }[];
    },
    repeaterRequired: boolean,
  ) {
    const hasFollowUpQuestions =
      questionFormGroup.contains('followUpQuestions') &&
      (
        questionFormGroup.get('followUpQuestions') as FormArray<
          ControlsOf<FormQuestion>
        >
      ).controls;
    if (hasFollowUpQuestions) {
      for (const followUpQuestionControl of (
        questionFormGroup.get('followUpQuestions') as FormArray<
          ControlsOf<FormQuestion>
        >
      ).controls) {
        if (followUpQuestionControl.disabled) {
          continue;
        }
        const followUpQuestion = section.questions.find(
          (q) => q.id === followUpQuestionControl.value.id,
        );

        this.setQuestionFailed(
          followUpQuestion,
          followUpQuestionControl,
          section,
          validationSection,
          repeaterRequired,
        );
      }
    }

    if (!hasFollowUpQuestions && questionFormGroup.invalid) {
      const invalidSubquestions = [];
      if (question.type === QuestionType.Repeater) {
        const repeatables = questionFormGroup.get(
          'repeatables',
        ) as FormArray<FormRepeatable>;

        let index = 0;
        for (const repeatable of repeatables.controls) {
          for (const control of repeatable.get('questions').controls) {
            if (control.valid) {
              continue;
            }

            const subQuestion = section.questions.find(
              (q) => q.id === control.value.id,
            );

            invalidSubquestions.push({
              title: this.getQuestionTitle(subQuestion),
              id: subQuestion.id,
              repeaterId: index,
              isRequiredFailed: this.isRequiredFailed(
                subQuestion,
                control,
                false,
              ),
            });
          }

          index = index + 1;
        }
      }

      const questionValidation = {
        title: this.getQuestionTitle(question),
        id: question.id,
        isRequiredFailed: this.isRequiredFailed(
          question,
          questionFormGroup,
          repeaterRequired,
        ),
        invalidSubquestions: invalidSubquestions,
      };

      validationSection.questions.push(questionValidation);
    }
  }

  isRepeaterRequiredFailed(
    formControl: FormControl<ControlsOf<FormQuestionRepeater>>,
    questionStructure: QuestionStructureRepeater,
  ) {
    if (
      questionStructure.minNumberOfRepeatables &&
      (!formControl.value.repeatables ||
        formControl.value.repeatables.length === 0)
    ) {
      return true;
    }

    let allEmpty = true;
    let anyRequired = false;
    for (const repeatable of (
      formControl.get('repeatables') as FormArray<FormRepeatable>
    ).controls) {
      for (const question of repeatable.get('questions').controls) {
        if ((question.value as FormQuestionStandard).value) {
          allEmpty = false;
          break;
        }

        if (question.hasError('required')) {
          anyRequired = true;
        }
      }

      if (!allEmpty) {
        break;
      }
    }

    return allEmpty && anyRequired;
  }

  private isRequiredFailed(
    question: Question,
    questionFormGroup: FormGroup<ControlsOf<FormBudget | FormQuestion>>,
    repeaterRequired: boolean,
  ) {
    return question.type === QuestionType.Budget
      ? !(questionFormGroup.value as FormBudget).projektPartAndDateValid
      : repeaterRequired ||
          (question.validation.required &&
            !(questionFormGroup.value as FormQuestionStandard).value);
  }

  private getSectionTitle(section: Section, sectionNumber: number) {
    return `${sectionNumber}. ${this.translatePipe.transform(
      section.translations?.list['title'],
      this.translateService.activeLang,
      section.title,
    )}`;
  }

  private getQuestionTitle(question: Question) {
    const title = this.questionNumberService.getShowQuestionNumber(
      question.type,
    )
      ? `${this.questionNumberService.getQuestionNumber(
          question.id,
        )}. ${this.translatePipe.transform(
          question.translations?.list['title'],
          this.translateService.activeLang,
          question.title,
        )}`
      : this.translatePipe.transform(
          question.translations?.list['title'],
          this.translateService.activeLang,
          question.title,
        );

    if (
      title === 'Projektledare, begäran om ändring' ||
      title === 'Project manager, change request'
    ) {
      return this.translate.translate('person_project_manager');
    } else if (
      title === 'Firmatecknare, begäran om ändring' ||
      title === 'Company signatory, change request'
    ) {
      return this.translate.translate('person_signatory');
    }
    return title;
  }
}
